import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { LayoutHeader, LayoutHeaderProps } from 'src/components/common/LayoutHeader';
import { UiTextTooltip } from 'src/components/UI/TextTooltip';
import { ROUTE_START_GUIDE_ORGANIZATION } from 'src/routes';
import { ChildrenPropType } from 'src/utils/types';
import { UiButton } from 'src/components/UI/Button';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';

type Props = Pick<LayoutHeaderProps, 'header' | 'title'> & {
  children: ChildrenPropType;

  onSaveClick?: () => void;
  isSaving?: boolean;
  isSavingViewHomePage?: boolean;
  saveButtonDisabled?: boolean;
  viewHomePage?: () => void;
  isNewDesign?: boolean;
  saveTooltipText?: string;

  saveText?: string;
  cancelText?: string;
};

export const OrganizationLayout: FC<Props> = ({
  header,
  title,
  children,
  onSaveClick,
  isSaving,
  isSavingViewHomePage,
  saveButtonDisabled,
  viewHomePage,
  isNewDesign,
  saveTooltipText,
  saveText,
  cancelText,
}) => {
  const { t } = useTranslation();

  const showFooter = !!(onSaveClick || viewHomePage);

  const submitButton = (
    <UiButton primary loading={isSaving} onClick={onSaveClick} disabled={saveButtonDisabled} dataTestId="submit">
      {saveText || t('common.save')}
    </UiButton>
  );

  return (
    <>
      <LayoutHeader header={header} title={title} isNewDesign={isNewDesign} />
      <div className="public-page" id="organizationSetup">
        <div className="main-wrapper">
          <div id="organization-setup-content">
            <div id="setup">{children}</div>
          </div>
        </div>

        {showFooter && (
          <div className="setup-nav-gray-ribbon SecondaryBrandColor">
            <div className="container">
              <div className="row">
                <div className="col-xs-12">
                  <div className="spp-buttons spp-buttons-custom">
                    <div className="hover-container">
                      <UiInnerNotSPALink
                        href={ROUTE_START_GUIDE_ORGANIZATION}
                        className="btn BtnSecondary noUseBeforeunload"
                      >
                        {cancelText || t('common.cancel')}
                      </UiInnerNotSPALink>
                    </div>
                    <div className="hover-container">
                      {saveTooltipText ? (
                        <UiTextTooltip showHover text={saveTooltipText} wrapperClassName="d-flex">
                          {submitButton}
                        </UiTextTooltip>
                      ) : (
                        submitButton
                      )}
                    </div>
                    {viewHomePage && (
                      <div className="hover-container">
                        <UiButton
                          secondary
                          loading={isSavingViewHomePage}
                          onClick={viewHomePage}
                          className="noUseBeforeunload"
                          dataTestId="viewHomePage"
                        >
                          {t('organization.viewPage')}
                        </UiButton>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
